// src/components/layouts/AppLayout.jsx
import React, { useState, useCallback, memo, useMemo } from 'react';
import TopNavBar from '../dashboard/TopNavBar';
import ChatWindow from '../dashboard/ChatWindow';

const AppLayout = memo(({ 
  children,
  handleSignOut,
  currentUser,
  currentUserRoleInSelectedInstitution,
  selectedInstitution,
  globalUnreadCount,
  onUnreadCountChange,
  markMessagesAsReadForUser,
  clearUserFromSessionTracking
}) => {
  // Move chat state to AppLayout instead of App.jsx
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  const toggleChatSidebar = useCallback(() => {
    setIsChatOpen(prev => !prev);
  }, []);

  // Memoize children to prevent re-renders when chat state changes
  const memoizedChildren = useMemo(() => children, [
    // Only re-render children if the actual component type or key props change
    children?.type,
    children?.props?.sessionType,
    children?.props?.initialTab,
    children?.key
  ]);

  return (
    <div className="flex flex-col h-screen bg-slate-50">
      <TopNavBar
        toggleChatSidebar={toggleChatSidebar}
        isChatOpen={isChatOpen}
        handleSignOut={handleSignOut}
        currentUser={currentUser}
        userRoleInInstitution={currentUserRoleInSelectedInstitution}
        institutionName={selectedInstitution?.name}
        globalUnreadCount={globalUnreadCount}
        onUnreadCountUpdate={onUnreadCountChange}
      />
      
      <main className="flex-1 overflow-y-auto relative">
        {memoizedChildren}
      </main>
      
      {isChatOpen && (
        <ChatWindow 
          isOpen={isChatOpen}
          currentUser={currentUser}
          onClose={toggleChatSidebar}
          onUnreadCountChange={onUnreadCountChange}
          markMessagesAsReadForUser={markMessagesAsReadForUser}
          clearUserFromSessionTracking={clearUserFromSessionTracking}
        />
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function - only re-render when important props change
  return (
    // Check structural props
    prevProps.handleSignOut === nextProps.handleSignOut &&
    prevProps.currentUser?.id === nextProps.currentUser?.id &&
    prevProps.currentUserRoleInSelectedInstitution === nextProps.currentUserRoleInSelectedInstitution &&
    prevProps.selectedInstitution?.id === nextProps.selectedInstitution?.id &&
    // CRITICAL: Check globalUnreadCount so TopNavBar receives updates
    prevProps.globalUnreadCount === nextProps.globalUnreadCount &&
    // Check if children are the same
    prevProps.children?.type === nextProps.children?.type &&
    prevProps.children?.props?.sessionType === nextProps.children?.props?.sessionType &&
    prevProps.children?.props?.initialTab === nextProps.children?.props?.initialTab &&
    // Check callbacks are same reference (they should be stable)
    prevProps.markMessagesAsReadForUser === nextProps.markMessagesAsReadForUser &&
    prevProps.clearUserFromSessionTracking === nextProps.clearUserFromSessionTracking &&
    prevProps.onUnreadCountChange === nextProps.onUnreadCountChange
  );
});

AppLayout.displayName = 'AppLayout';

export default AppLayout;